/**
 * Session Debug Initializer
 *
 * Utility to initialize session debugging based on environment variables,
 * localStorage, or URL parameters.
 */

import { sessionDebugger, DEBUG_LEVELS } from './session-debug';

class SessionDebugInitializer {
  constructor() {
    this.initialized = false;
  }

  /**
   * Initialize debugging based on various sources (URL params, localStorage, env vars)
   */
  init() {
    if (this.initialized) return;

    const urlParams = new URLSearchParams(window.location.search);
    const localStorageDebug = localStorage.getItem('virtius_session_debug');
    const envDebug = process.env.REACT_APP_SESSION_DEBUG;

    // Check URL parameters first (highest priority)
    if (urlParams.has('debug_session')) {
      const debugLevel = this.parseDebugLevel(urlParams.get('debug_session'));
      this.enableDebug(debugLevel);
      console.log(
        '[SessionDebugInitializer] Debug enabled via URL parameter:',
        debugLevel
      );
      return;
    }

    // Check localStorage (medium priority)
    if (localStorageDebug) {
      const debugLevel = this.parseDebugLevel(localStorageDebug);
      this.enableDebug(debugLevel);
      console.log(
        '[SessionDebugInitializer] Debug enabled via localStorage:',
        debugLevel
      );
      return;
    }

    // Check environment variable (lowest priority)
    if (envDebug) {
      const debugLevel = this.parseDebugLevel(envDebug);
      this.enableDebug(debugLevel);
      console.log(
        '[SessionDebugInitializer] Debug enabled via environment variable:',
        debugLevel
      );
      return;
    }

    // Do not auto-enable in development; require explicit URL/localStorage/env
    this.initialized = true;
  }

  /**
   * Parse debug level from string input
   */
  parseDebugLevel(input) {
    if (!input) return DEBUG_LEVELS.BASIC;

    const inputUpper = input.toString().toUpperCase();

    // Map string values to debug levels
    switch (inputUpper) {
      case 'NONE':
      case '0':
      case 'FALSE':
        return DEBUG_LEVELS.NONE;
      case 'BASIC':
      case '1':
      case 'TRUE':
        return DEBUG_LEVELS.BASIC;
      case 'DETAILED':
      case '2':
        return DEBUG_LEVELS.DETAILED;
      case 'VERBOSE':
      case '3':
        return DEBUG_LEVELS.VERBOSE;
      default:
        return DEBUG_LEVELS.BASIC;
    }
  }

  /**
   * Enable debugging with specified level
   */
  enableDebug(level) {
    sessionDebugger.setDebugLevel(level);
    sessionDebugger.enable();
  }

  /**
   * Disable debugging
   */
  disableDebug() {
    sessionDebugger.disable();
    localStorage.removeItem('virtius_session_debug');
  }

  /**
   * Toggle debug mode on/off
   */
  toggleDebug(level = DEBUG_LEVELS.BASIC) {
    if (sessionDebugger.isEnabled) {
      this.disableDebug();
      console.log('[SessionDebugInitializer] Debug disabled');
    } else {
      this.enableDebug(level);
      localStorage.setItem('virtius_session_debug', level.toString());
      console.log('[SessionDebugInitializer] Debug enabled with level:', level);
    }
  }

  /**
   * Get current debug status
   */
  getStatus() {
    return {
      enabled: sessionDebugger.isEnabled,
      level: sessionDebugger.debugLevel,
      levelName: Object.keys(DEBUG_LEVELS).find(
        (key) => DEBUG_LEVELS[key] === sessionDebugger.debugLevel
      ),
    };
  }
}

// Create singleton instance
const sessionDebugInit = new SessionDebugInitializer();

// Expose toggle function to window for console access
if (typeof window !== 'undefined') {
  window.toggleSessionDebug = (level) => sessionDebugInit.toggleDebug(level);
  window.sessionDebugStatus = () => sessionDebugInit.getStatus();
}

export default sessionDebugInit;
